import type { BaseTimePickerProps } from '../components/BaseTimePicker';
import type { DateTimeFilter } from '~/presentation/ui';

import { getMonthAndYearByDateFilter, useDateTimePickerContext } from '~/presentation/ui';
import { useDateTimeFilter } from './useDateTimeFilter';
import { useEffect, useState } from 'react';
import { dayjs } from '~/utils/dayjs';

export interface IDateRangeState {
  alias?: DateTimeFilter['alias'];
  start: Date;
  end: Date;
}

export interface IDateTimePickerProps {
  ranges?: DateTimeFilter[];
  initialPicked?: DateTimeFilter;
  onApply?: BaseTimePickerProps['onApply'];
}

export const useDateTimePicker = ({ ranges, initialPicked, onApply }: IDateTimePickerProps) => {
  const { DATE_TIME_FILTERS } = useDateTimeFilter();
  const allRanges = ranges || DATE_TIME_FILTERS;

  const { dateTimePicked, setDateTimePicked } = useDateTimePickerContext();

  const [dateRange, setDateRange] = useState<DateTimeFilter>(dateTimePicked);

  const [{ month, year }, setDate] = useState({
    month: dayjs(dateTimePicked.since).month(),
    year: dayjs(dateTimePicked.since).year(),
  });

  useEffect(() => {
    if (dateTimePicked) {
      const untilDate = new Date(dateTimePicked.until);
      const monthDiff = (
        referenceDate: { year: number; month: number },
        newDate: { year: number; month: number },
      ) => {
        return newDate.month - referenceDate.month + 12 * (referenceDate.year - newDate.year);
      };
      const _monthDifference = monthDiff(
        { year, month },
        {
          year: untilDate.getFullYear(),
          month: untilDate.getMonth(),
        },
      );
      // if (monthDifference > 1 || monthDifference < 0) {
      //   setDate({
      //     month: untilDate.getMonth(),
      //     year: untilDate.getFullYear(),
      //   });
      // }
    }
  }, [dateTimePicked, month, year]);

  const onMonthChange = (month: number, year: number) => {
    setDate({ month, year });
  };

  const onCalendarChange = (value: IDateRangeState) => {
    const { start, end, alias } = value;

    const customDateRange: DateTimeFilter = {
      alias: 'custom',
      title: 'Custom',
      since: start.getTime(),
      until: end.getTime(),
    };

    const newDateRange = allRanges.find((range) => {
      const isSameAlias = range.alias === alias;
      const isSameUntil = range.until === end.getTime();
      const isSameSince = range.since === start.getTime();
      // const isSameVersionId = !!range.versionId && !!versionId && range.versionId === versionId;

      const isSameTime = isSameSince && isSameUntil;
      return isSameAlias || isSameTime;
    });

    const finalDateRange = newDateRange || initialPicked || customDateRange;
    setDateRange(finalDateRange);
  };

  const onChange = ({ end }: { start: Date; end: Date }) => {
    setDate(getMonthAndYearByDateFilter(month, year, end));
  };

  const apply = () => {
    setDateTimePicked(dateRange);
    onApply?.(dateRange);
  };

  const cancel = () => {
    setDateRange(dateTimePicked);
  };

  useEffect(() => {
    if (dateTimePicked) {
      setDateRange(dateTimePicked);

      setDate({
        month: dayjs(dateTimePicked.since).month(),
        year: dayjs(dateTimePicked.since).year(),
      });
    }
  }, [dateTimePicked]);

  return {
    month,
    year,
    setDate,
    dateRange,
    apply,
    cancel,
    dateTimePicked,
    onChange,
    onMonthChange,
    onCalendarChange,
  };
};
